/**
 * Pachisi Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → cowrie throw → move selection →
 * move execution → animation → turn management → win.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { PachisiEngine } from '../PachisiEngine';
import { PachisiAI } from '../PachisiAI';
import { PachisiBoard } from './PachisiBoard';
import { PachisiCowries } from './PachisiCowries';
import { MoveHistory } from './MoveHistory';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { PachisiErrorHandler } from '../services/PachisiErrorHandler';
import { PachisiStatisticsService } from '../services/PachisiStatisticsService';
import { PachisiGamePersistence } from '../services/PachisiGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  PachisiGameSettings,
  PachisiGameState,
  PachisiMove,
  MoveResult,
  CowrieResult,
  PlayerColor,
  PlayerType,
  PawnIdentifier,
  PachisiGameProps,
  PLAYER_COLORS,
} from '../types';
import './PachisiGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';
type GamePhase = 'waiting' | 'throwing' | 'moving' | 'animating' | 'completed';

const PachisiGameInner: React.FC<PachisiGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<PachisiGameSettings | null>(null);
  const [gameState, setGameState] = useState<PachisiGameState | null>(null);
  const [cowrieResult, setCowrieResult] = useState<CowrieResult | null>(null);
  const [throwing, setThrowing] = useState(false);
  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [highlightedPawns, setHighlightedPawns] = useState<PawnIdentifier[]>([]);
  const [lastMoveResult, setLastMoveResult] = useState<MoveResult | null>(null);

  // Refs
  const engineRef = useRef<PachisiEngine>(new PachisiEngine());
  const aiRef = useRef<PachisiAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());

  // Services (lazy init)
  const errorHandlerRef = useRef<PachisiErrorHandler | null>(null);
  const statsServiceRef = useRef<PachisiStatisticsService | null>(null);
  const persistenceRef = useRef<PachisiGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new PachisiErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new PachisiStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new PachisiGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Pachisi services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: PachisiGameSettings) => {
      const engine = engineRef.current;
      const count = newSettings.playerCount;

      // Build player types array
      const playerTypes: PlayerType[] = [];
      if (newSettings.mode === 'singleplayer') {
        playerTypes.push('human');
        for (let i = 0; i < (newSettings.aiOpponentCount || 1); i++) {
          playerTypes.push('ai');
        }
      } else {
        for (let i = 0; i < count; i++) {
          playerTypes.push('human');
        }
      }

      engine.initializeGame(count, playerTypes, newSettings);

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setCowrieResult(null);
      setThrowing(false);
      setGamePhase('waiting');
      setHighlightedPawns([]);
      setLastMoveResult(null);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('pachisi');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new PachisiAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `Pachisi ${newSettings.mode === 'singleplayer' ? 'single player' : 'multiplayer'} game started!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleThrowCowries = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || throwing || gamePhase === 'animating' || gamePhase === 'moving') return;

    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;
    const playerType = state.playerTypes[currentPlayer];

    // In single player, only allow human to throw manually
    if (settings?.mode === 'singleplayer' && playerType === 'ai') {
      return;
    }

    setThrowing(true);
    setGamePhase('throwing');
    const result = engine.throwCowries();

    // Show throwing animation, then show valid moves
    setTimeout(() => {
      setCowrieResult(result);
      setThrowing(false);

      // Get valid moves
      const validMoves = engine.getValidMoves(currentPlayer);

      if (validMoves.length === 0) {
        // No valid moves — auto-skip
        addNotification({
          type: NotificationType.INFO,
          title: 'No Valid Moves',
          message: `${currentPlayer} has no valid moves. Turn skipped.`,
          duration: 2000,
        });

        setTimeout(() => {
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setHighlightedPawns([]);
          setCowrieResult(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 800);
        return;
      }

      // Highlight valid pawns
      const highlighted: PawnIdentifier[] = validMoves.map((m) => ({
        player: m.player,
        pawnIndex: m.pawnIndex,
      }));
      // Deduplicate
      const seen = new Set<string>();
      const unique = highlighted.filter((t) => {
        const key = `${t.player}-${t.pawnIndex}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setHighlightedPawns(unique);
      setGamePhase('moving');
      setGameState(engine.getGameState());
    }, 800);
  }, [throwing, gamePhase, settings, addNotification]);

  const handlePawnSelect = useCallback(
    (player: PlayerColor, pawnIndex: number) => {
      const engine = engineRef.current;
      if (engine.isGameOver() || gamePhase !== 'moving') return;

      const state = engine.getGameState();
      if (state.currentTurn !== player) return;

      const validMoves = engine.getValidMoves(player);
      const move = validMoves.find((m) => m.pawnIndex === pawnIndex);
      if (!move) return;

      executeMove(move);
    },
    [gamePhase]
  );

  const executeMove = useCallback(
    (move: PachisiMove) => {
      const engine = engineRef.current;
      setGamePhase('animating');
      setHighlightedPawns([]);

      try {
        const result = engine.executeMove(move.player, move);
        setLastMoveResult(result);

        if (!result.success) {
          errorHandlerRef.current?.handleGameLogicError(result.error || 'Move failed');
          setGamePhase('waiting');
          return;
        }

        // Show notifications for special events
        if (result.captured) {
          addNotification({
            type: NotificationType.WARNING,
            title: '💥 Capture!',
            message: `${move.player} captured ${result.captured.player}'s pawn ${result.captured.pawnIndex + 1}!`,
            duration: 2500,
          });
        }

        if (result.reachedHome) {
          addNotification({
            type: NotificationType.SUCCESS,
            title: '🏠 Home!',
            message: `${move.player}'s pawn ${move.pawnIndex + 1} reached home!`,
            duration: 2500,
          });
        }

        if (result.extraTurn) {
          addNotification({
            type: NotificationType.INFO,
            title: '🐚 Grace Throw!',
            message: `${move.player} gets an extra turn!`,
            duration: 2000,
          });
        }

        // After animation delay, advance turn
        setTimeout(() => {
          if (result.gameOver) {
            handleGameOver(result);
            return;
          }

          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setCowrieResult(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 600);
      } catch (error) {
        setGamePhase('waiting');
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification]
  );

  const scheduleAITurn = useCallback(
    (state: PachisiGameState) => {
      if (!aiRef.current) return;
      const currentPlayer = state.currentTurn;
      const playerType = state.playerTypes[currentPlayer];
      if (playerType !== 'ai') return;
      if (state.gameStatus.type !== 'active') return;

      aiRef.current.scheduleAction(() => {
        handleAITurn();
      });
    },
    []
  );

  const handleAITurn = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver() || throwing) return;

    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;

    // Throw cowries
    setThrowing(true);
    setGamePhase('throwing');
    const result = engine.throwCowries();

    setTimeout(() => {
      setCowrieResult(result);
      setThrowing(false);

      const validMoves = engine.getValidMoves(currentPlayer);

      if (validMoves.length === 0) {
        addNotification({
          type: NotificationType.INFO,
          title: 'AI Turn',
          message: `AI (${currentPlayer}) has no valid moves. Turn skipped.`,
          duration: 1500,
        });

        setTimeout(() => {
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);
          setGamePhase('waiting');
          setCowrieResult(null);

          autoSaveGame(newState);
          scheduleAITurn(newState);
        }, 600);
        return;
      }

      // AI selects move
      const aiMove = aiRef.current!.selectMove(engine, currentPlayer, result.moveValue, validMoves);

      // Brief highlight then execute
      setHighlightedPawns([{ player: aiMove.player, pawnIndex: aiMove.pawnIndex }]);

      setTimeout(() => {
        executeMove(aiMove);
      }, 400);
    }, 500);
  }, [throwing, addNotification, executeMove, scheduleAITurn]);

  const autoSaveGame = useCallback(
    (state: PachisiGameState) => {
      if (persistenceRef.current && settings) {
        persistenceRef.current.autoSave(settings.mode, state, {
          aiDifficulty: settings.aiDifficulty,
          startedAt: state.startedAt,
          lastSavedAt: Date.now(),
        });
      }
    },
    [settings]
  );

  const handleGameOver = useCallback(
    (result: MoveResult) => {
      const engine = engineRef.current;
      const state = engine.getGameState();
      setGameState(state);
      setGamePhase('completed');
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings && result.winner) {
        const capturesMade = state.moveHistory.filter((m) => m.captured).length;
        const graceThrows = state.moveHistory.filter((m) => m.extraTurn).length;

        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            playerCount: state.playerCount,
            winner: result.winner,
            reason: 'all_pawns_home',
            totalMoves: state.moveHistory.length,
            capturesMade,
            graceThrows,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          settings.mode === 'singleplayer' ? 'red' : result.winner,
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 Game Over!',
        message: `${result.winner} wins the game!`,
        duration: 5000,
      });
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setCowrieResult(null);
    setLastMoveResult(null);
    setSettings(null);
    setHighlightedPawns([]);
    setGamePhase('waiting');
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial turn if AI goes first
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const state = engine.getGameState();
    const currentPlayer = state.currentTurn;
    const playerType = state.playerTypes[currentPlayer];
    if (playerType === 'ai' && !engine.isGameOver() && !throwing && gamePhase === 'waiting') {
      aiRef.current.scheduleAction(() => handleAITurn());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const pachisiConfig: GameModeConfig = {
      gameName: 'Pachisi',
      gameIcon: '🎲',
      subtitle: 'Ancient Indian board game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          playerCount: 2,
          aiOpponentCount: 1,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          playerCount: 2,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', playerCount: 2 });
      }
    };

    return (
      <div className="pachisi-game">
        <UniversalGameModeSelector
          config={pachisiConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.currentTurn;
  const currentPlayerType = gameState.playerTypes[currentPlayer];
  const isHumanTurn = currentPlayerType === 'human';
  const isCowriesDisabled = !isHumanTurn || throwing || gamePhase !== 'waiting' || gameState.gameStatus.type !== 'active';

  // Game stats for game-over screen
  const capturesMade = gameState.moveHistory.filter((m) => m.captured).length;
  const graceThrows = gameState.moveHistory.filter((m) => m.extraTurn).length;

  // Game over config
  const gameOverConfig = (view === 'game-over' && gameState.gameStatus.type === 'win') ? {
    show: true,
    result: 'win' as 'win' | 'loss' | 'draw',
    title: '🎉 Game Over!',
    subtitle: `${gameState.gameStatus.winner} wins! (${gameState.moveHistory.length} moves, ${capturesMade} captures, ${graceThrows} grace throws)`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Pachisi',
    board: (
      <div className="pachisi-game__board-area">
        <PachisiBoard
          engine={engineRef.current}
          onPawnSelect={handlePawnSelect}
          highlightedPawns={highlightedPawns}
          currentTurn={currentPlayer}
          isInteractive={isHumanTurn && gamePhase === 'moving'}
          playerCount={gameState.playerCount}
          gamePhase={gamePhase}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'players',
        title: 'Players',
        icon: '👥',
        content: (
          <div className="pachisi-game__player-info" role="status" aria-live="polite">
            <div className="pachisi-game__positions">
              {gameState.activePlayers.map((color) => (
                <div
                  key={color}
                  className={`pachisi-game__player-row ${color === currentPlayer ? 'pachisi-game__player-row--active' : ''}`}
                >
                  <span className={`pachisi-game__player-dot pachisi-game__player-dot--${color}`} aria-hidden="true" />
                  <span className="pachisi-game__player-name">
                    {gameState.playerTypes[color] === 'ai' ? `AI (${color})` : color}
                  </span>
                  <span className="pachisi-game__player-home">
                    🏠 {gameState.pawnsHome[color]}/4
                  </span>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: 'cowries',
        title: 'Cowrie Shells',
        icon: '🐚',
        content: (
          <PachisiCowries
            shellsUp={cowrieResult?.shellsUp ?? null}
            moveValue={cowrieResult?.moveValue ?? null}
            throwing={throwing}
            disabled={isCowriesDisabled}
            onThrow={handleThrowCowries}
            playerColor={currentPlayer}
          />
        ),
      },
      {
        id: 'history',
        title: 'Move History',
        icon: '📜',
        content: <MoveHistory moves={gameState.moveHistory} maxHeight={180} />,
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          <span className={`pachisi-game__player-dot pachisi-game__player-dot--${currentPlayer}`} aria-hidden="true" />
          {' '}
          {currentPlayerType === 'ai'
            ? `AI (${currentPlayer}) is thinking...`
            : `${currentPlayer}'s turn`}
          {gamePhase === 'moving' && isHumanTurn && (
            <span className="pachisi-game__phase-hint"> — Select a pawn to move</span>
          )}
        </span>
      ),
      gameStatus: gameState.gameStatus.type === 'win' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus.type === 'win' ? 'ended' : 'active',
      aiThinking: currentPlayerType === 'ai',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="pachisi-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const PachisiGame: React.FC<PachisiGameProps> = (props) => (
  <ErrorBoundary componentName="Pachisi">
    <PachisiGameInner {...props} />
  </ErrorBoundary>
);

export { PachisiGame };
export default PachisiGame;
